import React, { useState, useEffect } from "react";
import { KioskProgram, KioskSlot } from "../../api/kioskAPI";
import { KioskSettings } from "./KioskMode";
import "./KioskSlotCards.css";

interface KioskSlotCardsProps {
  program: KioskProgram;
  onSelectSlot: (slot: KioskSlot) => void;
  onBack: () => void;
  settings: KioskSettings;
}

const KioskSlotCards: React.FC<KioskSlotCardsProps> = ({
  program,
  onSelectSlot,
  onBack,
  settings,
}) => {
  const [timeParts, setTimeParts] = useState<{ hour: string; minute: string; ampm: string }>({ hour: "", minute: "", ampm: "" });

  // Realtime clock with timezone support
  useEffect(() => {
    const updateClock = () => {
      try {
        const now = new Date();
        const hour = now.toLocaleTimeString("en-US", {
          timeZone: settings.timezone,
          hour: "numeric",
          hour12: true,
        }).replace(/\s*(AM|PM)$/i, "");
        const minute = now.toLocaleTimeString("en-US", {
          timeZone: settings.timezone,
          minute: "2-digit",
        });
        const ampm = now.toLocaleTimeString("en-US", {
          timeZone: settings.timezone,
          hour: "numeric",
          hour12: true,
        }).match(/(AM|PM)$/i)?.[0] || "";
        setTimeParts({ hour, minute, ampm });
      } catch {
        const now = new Date();
        const hour = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
        }).replace(/\s*(AM|PM)$/i, "");
        const minute = now.toLocaleTimeString("en-US", {
          minute: "2-digit",
        });
        const ampm = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
        }).match(/(AM|PM)$/i)?.[0] || "";
        setTimeParts({ hour, minute, ampm });
      }
    };

    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, [settings.timezone]);
  const formatTime = (time: string) => {
    // time is in format "HH:MM" or "HH:MM:SS"
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDays = (days: string[]) => {
    const dayNames: { [key: string]: string } = {
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat",
      sun: "Sun",
      monday: "Mon",
      tuesday: "Tue",
      wednesday: "Wed",
      thursday: "Thu",
      friday: "Fri",
      saturday: "Sat",
      sunday: "Sun",
    };

    return days
      .map((day) => dayNames[day.toLowerCase()] || day)
      .join(", ");
  };

  return (
    <div className="kiosk-screen kiosk-slots">
      <div className="kiosk-header">
        <button className="kiosk-header__back" onClick={onBack}>
          <span className="kiosk-header__back-icon">&#8249;</span>
          Back
        </button>
        <div className="kiosk-header__title">
          <h1>{program.label}</h1>
          <p>Select Your Time Slot</p>
        </div>
        <div className="kiosk-header__clock-wrapper">
          <span className="kiosk-header__clock kiosk-header__clock--static">
            {timeParts.hour}<span className="kiosk-clock__colon">:</span>{timeParts.minute} {timeParts.ampm}
          </span>
        </div>
      </div>

      <div className="kiosk-cards kiosk-slot-cards">
        {program.slots.map((slot) => (
          <button
            key={slot.id}
            className="kiosk-slot-card"
            onClick={() => onSelectSlot(slot)}
          >
            <div className="kiosk-slot-card__time">
              {slot.times && slot.times.length > 0 && slot.times[0] !== "x" ? (
                formatTime(slot.times[0])
              ) : (
                formatTime(slot.time)
              )}
            </div>
            <div className="kiosk-slot-card__details">
              {slot.days && slot.days.length > 0 && (
                <span className="kiosk-slot-card__days">
                  {formatDays(slot.days)}
                </span>
              )}
              {slot.durationHours && (
                <span className="kiosk-slot-card__duration">
                  {slot.durationHours} hour{slot.durationHours !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="kiosk-slot-card__arrow">&#8250;</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default KioskSlotCards;
